import { NEUTRAL_PILL_CLASS } from './statusPillColors'
import { formatDate } from './useFormattedDate'

// Label/badge lookup for AgentRepo's persisted aasm cadence state (#1536).
// Shared between AgentRepoProductOwner.vue (the /admin/agent-repos card)
// and ProductOwnerRunCard.vue (the /admin/product-owner-runs overview
// cards) so the label/color map lives in exactly one place, mirroring
// productOwnerRunStatus.ts for the run-status pill.
//
// `null` covers a repo with no AgentRepo db row at all yet (YAML-only),
// distinct from the persisted 'unstarted' default a row starts in before
// its first recompute.
export const PRODUCT_OWNER_CADENCE_STATES = ['unstarted', 'growing', 'flat', 'shrinking', 'bursting', 'idle'] as const

const CADENCE_STATE_LABELS: Record<string, string> = {
  unstarted: 'Not yet observed',
  growing: 'Growing',
  flat: 'Flat',
  shrinking: 'Shrinking',
  bursting: 'Bursting',
  idle: 'Idle',
}

export function productOwnerCadenceStateLabel(state: string | null | undefined): string {
  if (state == null) return 'Unknown'
  return CADENCE_STATE_LABELS[state] ?? state
}

// The pill FACE, not just its tooltip (#1625) — "Bursting · 12 min ago"
// rather than a bare "Bursting" that leaves a viewer to guess whether the
// spike is fresh or has sat there for a week. `enteredAt` is null for a row
// that has never actually transitioned (a brand-new record, or state ===
// null for a YAML-only repo), in which case the label renders bare.
export function productOwnerCadenceStateSinceLabel(
  state: string | null | undefined,
  enteredAt: string | null | undefined,
  now: Date,
): string {
  const label = productOwnerCadenceStateLabel(state)
  const since = formatDate(enteredAt, now)
  return since ? `${label} · ${since}` : label
}

// Only the two precedence-protected states (#1625) have a dedicated exit —
// every other state's own next daily recompute is its own "exit", not
// worth calling out on every hover.
const CADENCE_STATE_EXIT_CLAUSES: Record<string, string> = {
  bursting: 'Exits once the spike settles.',
  idle: 'Exits on the next run with net progress.',
}

export function productOwnerCadenceStateExitClause(state: string | null | undefined): string | null {
  if (state == null) return null
  return CADENCE_STATE_EXIT_CLAUSES[state] ?? null
}

// Same bg-COLOR-500/15 text-COLOR-300/90 pill convention as the run-status
// badge. Warm colors mean the algorithm is tightening (growing, bursting);
// cool ones mean it is relaxing (flat, shrinking, idle).
const CADENCE_STATE_BADGE_CLASSES: Record<string, string> = {
  unstarted: NEUTRAL_PILL_CLASS,
  growing: 'bg-amber-500/15 text-amber-300/90',
  flat: 'bg-emerald-500/15 text-emerald-300/90',
  shrinking: 'bg-teal-500/15 text-teal-300/90',
  bursting: 'bg-red-500/15 text-red-300/90',
  idle: 'bg-violet-500/15 text-violet-300/90',
}

export function productOwnerCadenceStateBadgeClass(state: string | null | undefined): string {
  if (state == null) return NEUTRAL_PILL_CLASS
  return CADENCE_STATE_BADGE_CLASSES[state] ?? NEUTRAL_PILL_CLASS
}
